/**
 * React Query hooks for the tenant payment-terms catalog. The list is small
 * and rarely changes, so a generous staleTime keeps the picker snappy.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentTermsApi } from './api';
import type { PaymentTerm, PaymentTermCreate, PaymentTermUpdate } from './types';

const PAYMENT_TERMS_KEY = ['payment-terms'] as const;

/**
 * Tenant payment-terms catalog. Always fetches the FULL catalog under one
 * stable cache key and narrows by `is_active` client-side via `select`, so
 * `usePaymentTerms()` and `usePaymentTerms(true)` share a single network
 * request instead of keying separate fetches off the filter argument.
 */
export function usePaymentTerms(isActive?: boolean) {
  return useQuery({
    queryKey: PAYMENT_TERMS_KEY,
    queryFn: () => paymentTermsApi.list(),
    staleTime: 5 * 60 * 1000,
    select:
      isActive === undefined
        ? undefined
        : (terms: PaymentTerm[]) => terms.filter((term) => term.is_active === isActive),
  });
}

export function useCreatePaymentTerm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PaymentTermCreate) => paymentTermsApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENT_TERMS_KEY }),
  });
}

export function useUpdatePaymentTerm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PaymentTermUpdate }) =>
      paymentTermsApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENT_TERMS_KEY }),
  });
}

export function useDeletePaymentTerm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentTermsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENT_TERMS_KEY }),
  });
}
